// Static data for Chat V2 — Slice 1 (visual port only)
// Mirrors v4-atrium-council.jsx + models.jsx + chat-bits.jsx

export const MODELS = {
  grace: {
    id: 'grace',
    name: 'Grace',
    role: 'General companion',
    description: 'Warm, pastoral encouragement, prayer, and everyday faith questions.',
    intro: "Hi, I'm Grace. Ask me anything tender or hard — I'll meet you where you are.",
    glyph: '☉',
    accent: '#e9c349',
    accentDim: '#c9a836',
    glow: 'rgba(233, 195, 73, 0.45)',
    eyebrow: 'GENERAL',
  },
  logos: {
    id: 'logos',
    name: 'Logos',
    role: 'Apologetics',
    description: 'Defending the faith — hard questions, evidence, and reasoned answers.',
    intro: "I'm Logos. Bring the skeptic's questions — let's reason together.",
    glyph: 'Λ',
    accent: '#f0d061',
    accentDim: '#b8964a',
    glow: 'rgba(155, 188, 240, 0.35)',
    eyebrow: 'APOLOGETICS',
  },
  spurgeon: {
    id: 'spurgeon',
    name: 'Spurgeon',
    role: 'Sermon prep',
    description: 'Sermon outlines, illustrations, and pastoral application.',
    intro: "I'm Spurgeon. Ready to help you preach what you're studying.",
    glyph: '✠',
    accent: '#e6b95a',
    accentDim: '#a87838',
    glow: 'rgba(212, 168, 100, 0.4)',
    eyebrow: 'SERMON PREP',
  },
  ezra: {
    id: 'ezra',
    name: 'Ezra',
    role: 'Bible study',
    description: 'Hebrew, Greek, exegesis, and cross-references for deep study.',
    intro: "I'm Ezra. Bring your hardest verse — we'll go word-by-word.",
    glyph: 'א',
    accent: '#dec06a',
    accentDim: '#9c8138',
    glow: 'rgba(140, 160, 220, 0.32)',
    eyebrow: 'BIBLE STUDY',
  },
}

export const MODEL_LIST = ['grace', 'logos', 'spurgeon', 'ezra'].map(id => MODELS[id])

// Voice id → existing chat-function `tab` value. The edge function already
// branches on `tab` to select the voice-specific system prompt, so multi-voice
// fan-out just sends N parallel calls each with the right tab. Inverse of the
// edge function's AI_TAB_TO_NAME map.
export const VOICE_TO_TAB = {
  grace: 'general',
  logos: 'apologetics',
  spurgeon: 'sermon_prep',
  ezra: 'bible_study',
}

// Reading lenses — toggleable hints the user adds to a question. Each lens
// injects a guidance fragment into the AI's system prompt so the response
// engages that perspective when relevant. After streaming, the AI emits
// [LENSES_USED:...] so we can show which it actually engaged.
//
// `description` shows in the hover tooltip on each chip — kept short so it
// reads at a glance.
export const ALL_LENSES = [
  {
    id: 'greek',
    label: 'Greek',
    short: 'Gk',
    description:
      'Surface key Greek words from the New Testament — the original-language nuance behind English translations.',
  },
  {
    id: 'hebrew',
    label: 'Hebrew',
    short: 'Heb',
    description:
      'Open up the Hebrew behind Old Testament terms — root meanings, wordplays, and what gets lost in translation.',
  },
  {
    id: 'crossref',
    label: 'Cross-refs',
    short: 'X-ref',
    description:
      'Pull 1–2 supporting passages elsewhere in Scripture that reinforce or illuminate the point.',
  },
  {
    id: 'historical',
    label: 'Historical',
    short: 'Hist',
    description:
      'Set the passage in its first-century (or biblical-era) cultural and historical context.',
  },
  {
    id: 'patristic',
    label: 'Patristic',
    short: 'Pat',
    description:
      'Surface how an early church father (Augustine, Athanasius, Chrysostom, etc.) read this passage.',
  },
  {
    id: 'narrative',
    label: 'Narrative',
    short: 'Nar',
    description:
      'Place the passage inside the larger biblical story — what comes before, what comes after, where it fits in redemption history.',
  },
]

// Bible version picker labels — derived from the canonical
// BIBLE_TRANSLATIONS list so the chat picker offers exactly what
// `resolveChapterVerses` can fetch (no broken ESV/NIV stubs that fail to
// load). Pair each label with its canonical id so callers can pass the
// id straight into the verse fetcher / pinned-passage block.
import { BIBLE_TRANSLATIONS as _BIBLE_TRANSLATIONS } from '@/data/bibleBooks'
export const BIBLE_VERSIONS = _BIBLE_TRANSLATIONS.map(t => t.label)
export const BIBLE_VERSION_IDS = _BIBLE_TRANSLATIONS.map(t => t.id)

export const BIBLE_BOOKS = [
  { group: 'Pentateuch', books: ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy'] },
  {
    group: 'History',
    books: ['Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings'],
  },
  { group: 'Wisdom', books: ['Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Songs'] },
  {
    group: 'Prophets',
    books: [
      'Isaiah',
      'Jeremiah',
      'Lamentations',
      'Ezekiel',
      'Daniel',
      'Hosea',
      'Joel',
      'Amos',
      'Obadiah',
      'Jonah',
      'Micah',
      'Nahum',
      'Habakkuk',
      'Zephaniah',
      'Haggai',
      'Zechariah',
      'Malachi',
    ],
  },
  { group: 'Gospels & Acts', books: ['Matthew', 'Mark', 'Luke', 'John', 'Acts'] },
  {
    group: 'Pauline',
    books: [
      'Romans',
      '1 Corinthians',
      '2 Corinthians',
      'Galatians',
      'Ephesians',
      'Philippians',
      'Colossians',
      '1 Thessalonians',
      '2 Thessalonians',
      '1 Timothy',
      '2 Timothy',
      'Titus',
      'Philemon',
    ],
  },
  {
    group: 'General',
    books: [
      'Hebrews',
      'James',
      '1 Peter',
      '2 Peter',
      '1 John',
      '2 John',
      '3 John',
      'Jude',
      'Revelation',
    ],
  },
]

const CHAPTER_COUNTS = {
  Genesis: 50,
  Exodus: 40,
  Leviticus: 27,
  Numbers: 36,
  Deuteronomy: 34,
  Psalms: 150,
  Proverbs: 31,
  Ecclesiastes: 12,
  'Song of Songs': 8,
  Isaiah: 66,
  Jeremiah: 52,
  Lamentations: 5,
  Ezekiel: 48,
  Daniel: 12,
  Matthew: 28,
  Mark: 16,
  Luke: 24,
  John: 21,
  Acts: 28,
  Romans: 16,
  '1 Corinthians': 16,
  '2 Corinthians': 13,
  Galatians: 6,
  Ephesians: 6,
  Philippians: 4,
  Colossians: 4,
  '1 Thessalonians': 5,
  '2 Thessalonians': 3,
  '1 Timothy': 6,
  '2 Timothy': 4,
  Titus: 3,
  Philemon: 1,
  Hebrews: 13,
  James: 5,
  '1 Peter': 5,
  '2 Peter': 3,
  '1 John': 5,
  '2 John': 1,
  '3 John': 1,
  Jude: 1,
  Revelation: 22,
}
export const chaptersFor = book => CHAPTER_COUNTS[book] || 50

export function fmtPassage(p) {
  if (!p) return ''
  const range = p.range && p.range.trim() ? `:${p.range}` : ''
  return `${p.book} ${p.chapter}${range}`
}

// Parse a textual reference like "Philippians 2:5-11" or "1 Corinthians 13"
// into the {book, chapter, range} shape chat-v2 uses for passage anchors.
// Returns null on anything we can't confidently parse so the caller can
// fall back gracefully.
export function parseAnchorRef(text) {
  if (!text || typeof text !== 'string') return null
  const trimmed = text.trim()
  // Match "<book name> <chapter>" with optional ":<verse[-range]>".
  // Book name allows leading "1 ", "2 ", "3 " (e.g., "1 Corinthians").
  const m = trimmed.match(/^([1-3]?\s?[A-Za-z][A-Za-z\s]*?)\s+(\d{1,3})(?::([\d–\-,\s]+))?$/)
  if (!m) return null
  const book = m[1].trim()
  const chapter = parseInt(m[2], 10)
  if (!book || !chapter) return null
  const range = m[3] ? m[3].trim() : ''
  return { book, chapter, range }
}

export const SAMPLE_HISTORY = [
  { id: 't1', model: 'grace', title: 'Anxiety in Philippians 4', when: 'Today', active: true },
  { id: 't2', model: 'ezra', title: 'The kenosis hymn — kenōthen', when: 'Tuesday' },
  { id: 't3', model: 'spurgeon', title: 'Sermon prep · Hebrews 11', when: 'Monday' },
  { id: 't4', model: 'logos', title: 'Suffering and a good God', when: 'Sunday' },
  { id: 't5', model: 'grace', title: 'Praying for an estranged sister', when: 'Last week' },
  { id: 't6', model: 'logos', title: 'Resurrection — historical case', when: 'Apr 18' },
  { id: 't7', model: 'ezra', title: 'Imago Dei in Genesis 1', when: 'Apr 14' },
  { id: 't8', model: 'spurgeon', title: 'Funeral homily on Romans 8', when: 'Apr 10' },
]

export const SAMPLE_CROSS_REFS = [
  { ref: 'John 1:14', q: 'And the Word was made flesh, and dwelt among us…' },
  { ref: '2 Cor 8:9', q: 'Though he was rich, yet for your sakes he became poor…' },
  { ref: 'Heb 2:14', q: 'He also himself likewise took part of the same…' },
  { ref: 'Isa 53:3', q: 'He is despised and rejected of men…' },
]

export const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'chat', label: 'Chat' },
  { id: 'learn', label: 'Learn' },
  { id: 'bible', label: 'Bible' },
  { id: 'prayer', label: 'Prayer' },
  { id: 'more', label: 'More' },
]

export const ADVANCED_OPTS = {
  modeToggle: true,
  voices: true,
  lenses: true,
  rightRail: true,
  history: true,
  rowActions: true,
}
// Simple mode hides the lens machinery + multi-voice, but Recents stays
// reachable so users can pick a past conversation, and Voices stays on
// in single-select mode so users can switch which voice they're talking
// to. ModeBar handles the simple/advanced label and toggleGuide returns
// `[id]` instead of toggling membership when complexity === 'simple'.
export const SIMPLE_OPTS = {
  modeToggle: false,
  voices: true,
  lenses: false,
  rightRail: false,
  history: true,
  rowActions: false,
}

// User-curated starter prompts keyed by `profile.primary_goal` (set during
// onboarding). When present, these win over MODEL_SUGGESTIONS so that the
// first thing a user sees in chat reflects WHY they signed up, not which
// voice happens to be active. Free users in simple mode see these too —
// the empty box is the activation killer; a few good prompts fixes that.
export const GOAL_STARTER_PROMPTS = {
  deepen_faith: [
    'How do I make prayer feel less like a chore?',
    'Walk me through a daily devotional rhythm',
    'What does it mean to abide in Christ?',
    'Help me read Psalm 23 slowly',
  ],
  understand_bible: [
    'Why does Genesis 1 have two creation accounts?',
    'Walk me through Romans 8 a piece at a time',
    "What's the difference between the Old and New Covenant?",
    'Help me understand parables in Matthew 13',
  ],
  defend_faith: [
    'How do we know the Bible is reliable?',
    'Steelman the problem of evil',
    'Make the historical case for the resurrection',
    "Respond to 'all religions lead to God'",
  ],
  sermon_prep: [
    'Outline a sermon on Hebrews 11',
    'Three illustrations for John 3:16',
    'Sermon series ideas in the Psalms',
    'Help me preach Romans 8 to a hurting church',
  ],
  answer_questions: [
    'Why does God allow suffering?',
    'Was Jesus really God, or just a great teacher?',
    'How do I reconcile science with Genesis?',
    'What about people who never heard the gospel?',
  ],
  explore_christianity: [
    'What do Christians actually believe?',
    'Who was Jesus, in plain terms?',
    "What's the difference between religion and the gospel?",
    'Where should I start reading the Bible?',
  ],
}

// Mobile-specific suggestion chips. Each persona's list is tuned to the
// thing that voice is actually best at — pastoral counsel for Grace,
// original-language depth for Ezra, apologetics for Logos, sermon prep
// for Spurgeon. Mix of short + medium lengths so the row reads varied.
export const MODEL_SUGGESTIONS = {
  grace: [
    "I'm anxious — what does Scripture say?",
    'Walk me through the Lord’s Prayer',
    'How do I forgive someone who hurt me?',
    'Help me grieve as a Christian',
  ],
  logos: [
    'How do we know the Bible is reliable?',
    'Steelman the problem of evil',
    'Make the historical case for the resurrection',
    "Respond to 'all religions lead to God'",
  ],
  spurgeon: [
    'Outline a sermon on Hebrews 11',
    'Three illustrations for John 3:16',
    'Sermon series ideas in the Psalms',
    'Help me preach Romans 8 to a hurting church',
  ],
  ezra: [
    "What does the Greek 'agape' really mean?",
    'Walk me through Romans 8:28 in the original',
    "What is the 'fear of the Lord' in Hebrew?",
    'Compare logos in John 1 across translations',
  ],
}

export const MOBILE_TABS = [
  { id: 'home', label: 'Home' },
  { id: 'chat', label: 'Chat' },
  { id: 'learn', label: 'Learn' },
  { id: 'bible', label: 'Bible' },
  { id: 'prayer', label: 'Prayer' },
  { id: 'more', label: 'More' },
]
